import { FC, useCallback, useEffect, useMemo } from "react";
import { FieldValues, useController, useFormContext } from "react-hook-form";

import { FieldBaseProps } from "capitalrx-components-2.0/lib/forms/FieldBaseProps";
import { useInputDisabled } from "capitalrx-components-2.0/lib/forms/InputGroup/useInputDisabled";
import { useInputReadonly } from "capitalrx-components-2.0/lib/forms/InputGroup/useInputReadonly";
import { SubmitGroup } from "capitalrx-components-2.0/lib/forms/SubmitGroup";
import { ValidityGroup } from "capitalrx-components-2.0/lib/forms/ValidityGroup";
import { VALIDATION } from "../../constants";

import { Input } from "./Input";
import { TelephoneTextboxProps } from "./TelephoneTextbox";
import { errorMessages } from "./errorMessages";
import { isValidPhoneNumberValue } from "./utils";

/**
 * The `Field` component properties.
 */
export interface FieldProps
  extends FieldBaseProps<string | null>,
  Pick<TelephoneTextboxProps, "countryCode" | "countryOptions" | "onCountryChange"> { }

/**
 * A React Hook Form compatible TelephoneTextbox Field component. For more information, see the documentation for
 * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
 */
export const Field: FC<FieldProps> = (props) => {
  const { name, required = false, defaultValue, validator, countryCode, ...restProps } = props;
  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);

  const validateHandler = useCallback(
    (value: string, formValues: FieldValues) => {
      const hasCountryCode = Boolean(countryCode);

      if (required) {
        if (value === null)
          return VALIDATION.REQUIRED;

        if (typeof value === "string" && value.length === 0)
          return VALIDATION.REQUIRED;
      }

      if (value !== null) {
        if (typeof value === "string" && value.trim().length > 0) {
          const isValid = isValidPhoneNumberValue(value, countryCode);
          if (!isValid) {
            return hasCountryCode ? errorMessages["Country invalid"] : errorMessages["International invalid"];
          }
        }
        if (typeof value !== "string") return errorMessages["invalid format"];
      }

      return validator ? validator(value, formValues) : true;
    },
    [required, validator, countryCode],
  );

  const rules = useMemo(
    () => ({
      validate: disabled || readOnly ? undefined : validateHandler,
    }),
    [disabled, readOnly, validateHandler],
  );

  const { resetField } = useFormContext();

  useEffect(() => {
    if (disabled) {
      resetField(name);
    }
  }, [disabled, readOnly, resetField, name]);

  const { field, fieldState, formState } = useController({
    name,
    defaultValue,
    rules,
  });

  const { ref, value, onChange, onBlur } = field;
  const { invalid, error } = fieldState;
  const { isSubmitting } = formState;

  const changeHandler = useCallback(
    (newValue: string | null) => {
      onChange(newValue);
    },
    [onChange],
  );

  return (
    <SubmitGroup submitting={isSubmitting}>
      <ValidityGroup invalid={invalid} error={error?.message}>
        <Input
          {...restProps}
          ref={ref}
          value={value}
          countryCode={countryCode}
          required={required}
          defaultValue={defaultValue}
          onChange={changeHandler}
          onFocusExit={onBlur}
        />
      </ValidityGroup>
    </SubmitGroup>
  );
};
